import React, { useRef, useEffect } from 'react'
import { map, isNil } from 'lodash'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import AlkemeIcon from 'images/services/branding/icons/alkeme-icon.svg'
import MustardIcon from 'images/services/branding/icons/mustard-icon.svg'
import BlitzIcon from 'images/services/branding/icons/blitz-icon.svg'
import LiveIcon from 'images/services/branding/icons/live-icon.svg'
import CreativelyIcon from 'images/services/branding/icons/creatively-icon.svg'
import BlackRifleIcon from 'images/services/branding/icons/black-rifle-icon.svg'
// import SomewhereGoodIcon from 'images/services/branding/icons/somewhere-good-icon.svg'
import SomewhereGoodIcon from 'images/services/branding/icons/somewhere-good-icon.webp'
import SpirituneIcon from 'images/services/branding/icons/spiritune-icon.svg'
import CrybIcon from 'images/services/branding/icons/cryb-icon.svg'
import ReferIcon from 'images/services/branding/icons/refer-icon.svg'
import BetqlIcon from 'images/services/branding/icons/betql-icon.svg'
import LovelleIcon from 'images/services/branding/icons/lovelle-icon.svg'
import FahloIcon from 'images/services/branding/icons/fahlo-icon.svg'

const Icons = [
  {
    name: 'alkeme',
    icon: AlkemeIcon,
    desktop: {
      posX: 0,
      posY: 2,
      velocity: 85,
    },
    mobile: {
      posX: -10,
      posY: 25,
      velocity: 100,
    },
  },
  {
    name: 'mustard',
    icon: MustardIcon,
    desktop: {
      posX: 35,
      posY: -5,
      velocity: 100,
    },
    mobile: {
      posX: 36.5,
      posY: -15,
      velocity: 100,
    },
  },
  {
    name: 'blitz',
    icon: BlitzIcon,
    desktop: {
      posX: -1,
      posY: 40,
      velocity: 85,
    },
    mobile: {
      posX: -14.5,
      posY: 58.5,
      velocity: 70,
    },
  },
  {
    name: 'live',
    icon: LiveIcon,
    desktop: {
      posX: 19.5,
      posY: 14.5,
      velocity: 70,
    },
    mobile: {
      posX: 19,
      posY: 48,
      velocity: 70,
    },
  },
  {
    name: 'black-rifle',
    icon: BlackRifleIcon,
    desktop: {
      posX: 35,
      posY: 55,
      velocity: 85,
    },
    mobile: {
      posX: 48.5,
      posY: 75,
      velocity: 70,
    },
  },
  {
    name: 'creatively',
    icon: CreativelyIcon,
    desktop: {
      posX: 12,
      posY: 65,
      velocity: 100,
    },
    mobile: {
      posX: 3.5,
      posY: 80,
      velocity: 100,
    },
  },
  {
    name: 'somewhere-good',
    icon: SomewhereGoodIcon,
    isImage: true,
    desktop: {
      posX: 52.5,
      posY: 18,
      velocity: 60,
    },
    mobile: {
      posX: 20,
      posY: 7.5,
      velocity: 65,
    },
  },
  {
    name: 'spiritune',
    icon: SpirituneIcon,
    desktop: {
      posX: 40,
      posY: 80,
      velocity: 100,
    },
    mobile: {
      posX: 52.5,
      posY: 102.5,
      velocity: 100,
    },
  },
  {
    name: 'cryb',
    icon: CrybIcon,
    desktop: {
      posX: 57.5,
      posY: 59.5,
      velocity: 100,
    },
    mobile: {
      posX: 77.5,
      posY: 52.5,
      velocity: 100,
    },
  },
  {
    name: 'refer',
    icon: ReferIcon,
    desktop: {
      posX: 68,
      posY: 12,
      velocity: 85,
    },
    mobile: {
      posX: 43,
      posY: 26.5,
      velocity: 70,
    },
  },
  {
    name: 'betql',
    icon: BetqlIcon,
    desktop: {
      posX: 87.5,
      posY: -5,
      velocity: 100,
    },
    mobile: {
      posX: 80,
      posY: 20,
      velocity: 100,
    },
  },
  {
    name: 'lovelle',
    icon: LovelleIcon,
    desktop: {
      posX: 86.5,
      posY: 25,
      velocity: 100,
    },
    mobile: {
      posX: 85,
      posY: -0.5,
      velocity: 70,
    },
  },
  {
    name: 'fahlo',
    icon: FahloIcon,
    desktop: {
      posX: 87.5,
      posY: 62.5,
      velocity: 70,
    },
    mobile: {
      posX: 82.5,
      posY: 80.5,
      velocity: 100,
    },
  },
]

gsap.registerPlugin(ScrollTrigger)

function Parallax() {
  const parallaxRef = useRef(null)

  const { m: isMobile } = useBreakpoint()

  useEffect(() => {
    if (!isNil(parallaxRef.current) && !isNil(isMobile)) {
      map(Icons, (icon) => {
        gsap.to(`.branding__parallax-icon.--${icon.name}`, {
          yPercent: `-${
            isMobile ? icon.mobile.velocity : icon.desktop.velocity
          }`,
          ease: 'none',
          scrollTrigger: {
            trigger: parallaxRef.current,
            start: 'top bottom', // the default value
            end: isMobile ? 'bottom+=25% top' : 'bottom top',
            // markers: icon.name === 'fahlo',
            scrub: true,
          },
        })
      })
    }
  }, [parallaxRef.current, isMobile])

  return (
    <div ref={parallaxRef} className="branding__parallax">
      {map(Icons, (icon) => (
        <div
          key={icon.name}
          className={`branding__parallax-icon --${icon.name}`}
          style={{
            top: `${isMobile ? icon.mobile.posY : icon.desktop.posY}%`,
            left: `${isMobile ? icon.mobile.posX : icon.desktop.posX}%`,
          }}
        >
          {icon.isImage ? (
            <img src={icon.icon} alt={icon.name} />
          ) : (
            <icon.icon />
          )}
        </div>
      ))}
    </div>
  )
}

export default Parallax
